import Button from 'components/Button';
import AdditionalInformationForm from 'components/Forms/AdditionalInformationForm';
import EventForm from 'components/Forms/EventForm';
import TaskForm from 'components/Forms/TaskForm';
import CandidateForm from 'components/admin/Candidate/CandidateForm';
import LicenseForm from 'components/admin/LicenseForm';
import ActivityType from 'core/enums/activityType';
import { ModalType } from 'core/useCases/modal/types';
import Bloc from 'images/Bloc.svg?react';
import CheckboxComplete from 'images/CheckboxComplete.svg?react';
import File from 'images/File.svg?react';
import Network from 'images/Network.svg?react';
import People from 'images/People.svg?react';
import Target from 'images/Target.svg?react';
import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useModalStore } from 'stores/modal';
import ActivityReportForm from '../Forms/ActivityReportForm';
import ApplicationForm from '../Forms/ApplicationForm';
import ApplicationReportForm from '../Forms/ApplicationReportForm';
import ApplicationsForm from '../Forms/ApplicationsForm';
import ContactForm from '../Forms/ContactForm';
import ContactsForm from '../Forms/ContactsForm';
import DeleteConfirmation from '../Forms/DeleteForm';
import PromptGeneratorForm from '../Forms/PromptGeneratorForm';
import PromptGeneratorOpportunityForm from '../Forms/PromptGeneratorOpportunityForm';
import AgencyForm from '../admin/Agency/AgencyForm';
import BaseModal from './BaseModal/BaseModal';
import InformationModal from './InformationModal';
import { SButtonsContainer } from './styles';

const computeImage = (type: ModalType): ReactNode => {
    switch (type) {
        // TODO Handle Application image
        case ModalType.Confirmation:
        case ModalType.Contact:
        case ModalType.Contacts:
            return <People />;
        case ModalType.Application:
        case ModalType.Agency:
        case ModalType.License:
        case ModalType.Candidate:
            return <File />;
        case ModalType.DeleteCandidate:
            return <People />;
        case ModalType.Task:
        case ModalType.TaskAdditionalInfo:
            return <Bloc />;
        // TODO Event can have many image for each event type
        case ModalType.Event:
        case ModalType.EventAdditionalInfo:
            return <Target />;
        case ModalType.ActivityReport:
        case ModalType.ApplicationReport:
            return <CheckboxComplete />;
        case ModalType.Information:
        case ModalType.PromptGenerator:
        case ModalType.PromptGeneratorOpportunity:
        case ModalType.Applications:
            return <Network />;
        default:
            throw Error('Wrong modal type in compute image method for modals');
    }
};

const Modals: FC = () => {
    const modalSettings = useModalStore((s) => s.modal);
    const closeModal = useModalStore((s) => s.closeModal);
    const { t } = useTranslation();

    if (!modalSettings) {
        return null;
    }

    const { cancelButtonLabel, confirmAction, defaultValues, disabledFields, title, type } = modalSettings;

    return (
        <BaseModal closeModal={closeModal} image={computeImage(type)} title={title}>
            {ModalType.Contact === type && (
                <ContactForm closeModal={closeModal} defaultValues={defaultValues} disabledFields={disabledFields} />
            )}

            {ModalType.Contacts === type && (
                <ContactsForm closeModal={closeModal} pasteMode={defaultValues?.pasteMode} />
            )}
            {ModalType.Task === type && (
                <TaskForm
                    allowSwitchToEvent={modalSettings.allowSwitchToEvent}
                    cancelButtonLabel={cancelButtonLabel}
                    closeModal={closeModal}
                    defaultValues={defaultValues}
                    disabledFields={disabledFields}
                />
            )}
            {ModalType.Event === type && (
                <EventForm closeModal={closeModal} defaultValues={defaultValues} disabledFields={disabledFields} />
            )}
            {ModalType.TaskAdditionalInfo === type && (
                <AdditionalInformationForm
                    activityType={ActivityType.Task}
                    closeModal={closeModal}
                    defaultValues={defaultValues}
                />
            )}
            {ModalType.EventAdditionalInfo === type && (
                <AdditionalInformationForm
                    activityType={ActivityType.Event}
                    closeModal={closeModal}
                    defaultValues={defaultValues}
                />
            )}
            {ModalType.ActivityReport === type && <ActivityReportForm closeModal={closeModal} data={defaultValues} />}
            {ModalType.Application === type && (
                <ApplicationForm
                    closeModal={closeModal}
                    defaultValues={defaultValues}
                    disabledFields={disabledFields}
                />
            )}
            {ModalType.ApplicationReport === type && (
                <ApplicationReportForm closeModal={closeModal} data={defaultValues} />
            )}
            {ModalType.Agency === type && (
                <AgencyForm closeModal={closeModal} defaultValues={defaultValues} disabledFields={disabledFields} />
            )}
            {ModalType.Candidate === type && (
                <CandidateForm closeModal={closeModal} defaultValues={defaultValues} disabledFields={disabledFields} />
            )}
            {ModalType.DeleteCandidate === type && (
                <DeleteConfirmation closeModal={closeModal} confirmAction={confirmAction} />
            )}
            {ModalType.License === type && <LicenseForm closeModal={closeModal} defaultValues={defaultValues} />}
            {ModalType.Information === type && (
                <InformationModal closeModal={closeModal} defaultValues={defaultValues} />
            )}
            {ModalType.PromptGenerator === type && <PromptGeneratorForm closeModal={closeModal} />}
            {ModalType.PromptGeneratorOpportunity === type && (
                <PromptGeneratorOpportunityForm closeModal={closeModal} />
            )}
            {ModalType.Applications === type && (
                <ApplicationsForm closeModal={closeModal} pasteMode={defaultValues?.pasteMode} />
            )}
            {ModalType.Confirmation === type && (
                <SButtonsContainer>
                    <Button onClick={closeModal} variant="text">
                        {t('common.cancel')}
                    </Button>
                    <Button
                        onClick={() => {
                            if (confirmAction) {
                                confirmAction();
                            }
                            closeModal();
                        }}
                    >
                        {t('common.confirm')}
                    </Button>
                </SButtonsContainer>
            )}
        </BaseModal>
    );
};

export default Modals;
